// App.js - Main Application Component
// This is the root component of our React application.
// It will manage the overall state, such as the list of sales,
// and render the different parts of our UI (form, table, stats, charts).

// --- IMPORTANT: Dependency Installation ---
// To resolve "Could not resolve" errors for 'chart.js' and 'react-chartjs-2',
// you MUST install these packages in your React project.
// Open your terminal in the project directory and run:
//
// npm install chart.js react-chartjs-2
// or
// yarn add chart.js react-chartjs-2
//
// After installation, your bundler (like Webpack or esbuild used by Create React App/Vite)
// will be able to find and include these modules in your application.
// ---

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useFirebaseAuth } from './context/FirebaseAuthContext';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Sales from './pages/Sales';
import Analytics from './pages/Analytics';
// TODO: Re-enable after implementing Reports Cloud Functions
// import Reports from './pages/Reports';
import Products from './pages/Products';
import Profile from './pages/Profile';
import UserManagement from './pages/UserManagement';
import Inventory from './pages/Inventory';
import Customers from './pages/Customers';
import Invoices from './pages/Invoices';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { user, loading } = useFirebaseAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }
    
    return user ? children : <Navigate to="/login" />;
};

// Error Fallback Component
const ErrorFallback = ({ error }) => {
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
            <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
                <h2 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h2>
                <p className="text-gray-600 mb-4">{error.message}</p>
                <button
                    onClick={() => window.location.reload()}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition duration-150"
                >
                    Reload Page
                </button>
            </div>
        </div>
    );
};

function App() {
    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Router>
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoute>
                                <Dashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/sales"
                        element={
                            <ProtectedRoute>
                                <Sales />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/analytics"
                        element={
                            <ProtectedRoute>
                                <Analytics />
                            </ProtectedRoute>
                        }
                    />
                    {/* TODO: Re-enable after implementing Reports Cloud Functions
                    <Route
                        path="/reports"
                        element={
                            <ProtectedRoute>
                                <Reports />
                            </ProtectedRoute>
                        }
                    />
                    */}
                    <Route
                        path="/products"
                        element={
                            <RoleProtectedRoute requiredPermission="products.view">
                                <Products />
                            </RoleProtectedRoute>
                        }
                    />
                    <Route
                        path="/profile"
                        element={
                            <ProtectedRoute>
                                <Profile />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/users"
                        element={
                            <RoleProtectedRoute requiredPermission="users.view">
                                <UserManagement />
                            </RoleProtectedRoute>
                        }
                    />
                    <Route
                        path="/inventory"
                        element={
                            <ProtectedRoute>
                                <Inventory />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/customers"
                        element={
                            <ProtectedRoute>
                                <Customers />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/invoices"
                        element={
                            <ProtectedRoute>
                                <Invoices />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/" element={<Navigate to="/dashboard" />} />
                </Routes>
            </Router>
        </ErrorBoundary>
    );
}

export default App;

// --- Setup Instructions (remain crucial) ---
// To make this runnable in a typical React environment (e.g., Create React App or Vite):
// 1. Save this code as App.js in your src/ folder.
// 2. Ensure you have a src/index.js like this:
//    import React from 'react';
//    import ReactDOM from 'react-dom/client';
//    import './index.css'; // For Tailwind base styles and utilities
//    import App from './App';
//
//    const root = ReactDOM.createRoot(document.getElementById('root'));
//    root.render(
//      <React.StrictMode>
//        <App />
//      </React.StrictMode>
//    );
// 3. Make sure your project has Tailwind CSS configured.
//    If not, follow Tailwind's installation guide for your specific React setup (Create React App, Vite, etc.).
//    e.g., https://tailwindcss.com/docs/guides/create-react-app
// 4. **Install chart.js and react-chartjs-2 (THIS IS THE FIX FOR THE ERROR):**
//    Open your project terminal and run:
//    npm install chart.js react-chartjs-2
//    OR
//    yarn add chart.js react-chartjs-2
// 5. Ensure your public/index.html has `<div id="root"></div>` and the Inter font link:
//    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
// 6. Add Tailwind's base styles to your src/index.css (or your main CSS file):
//    @tailwind base;
//    @tailwind components;
//    @tailwind utilities;
//    /* You might also want to add the chart-container and table-container styles here from the previous HTML version if not already handled by Tailwind utility classes or component-specific styles */
//    body { font-family: 'Inter', sans-serif; }
//    .chart-container { position: relative; height: 300px; width: 100%; max-width: 500px; margin: auto; }
//    .table-container::-webkit-scrollbar { width: 8px; }
//    .table-container::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
//    .table-container::-webkit-scrollbar-thumb { background: #888; border-radius: 10px; }
//    .table-container::-webkit-scrollbar-thumb:hover { background: #555; }

// Note on Tailwind and select dropdown arrows:
// The `appearance-none` class is used to remove default browser styling for select elements.
// The custom arrow is then added via inline style with an SVG. This is a common Tailwind pattern.
